// src/pages/kpis/compras/OtifProveedor.jsx
// KPI de OTIF Proveedor — pestañas Resumen / Histórico / Por proveedor / Detalle.
//
// Reglas del KPI (acordadas con Compras):
//  - Se mide por línea de OC (materia prima y empaque), por mes de la FECHA PROMETIDA.
//  - OTIF = la línea se completó (100%) en la fecha prometida o antes.
//  - Si llega de más cuenta como completa. Lo rechazado no cuenta como recibido.
//  - Evaluables = cumplidas + tarde + incompletas (vencidas sin completar,
//    o cerradas con entrega parcial porque el proveedor no completó).
//  - Aparte: por vencer, sin fecha prometida (Compras debe capturarla) y
//    cerradas sin nada recibido (canceladas por Dasavena).

import { useEffect, useMemo, useRef, useState } from 'react';
import { Chart, registerables } from 'chart.js';
import * as XLSX from 'xlsx';
import { FiDownload, FiRefreshCw, FiInfo, FiChevronLeft, FiChevronRight, FiAlertTriangle } from 'react-icons/fi';
import api from '../../../services/api';
import s from '../../mantenimiento/estilos';

Chart.register(...registerables);

const BASE = '/api/kpis/otif';
const COLOR = { verde: '#3E7C5A', amarillo: '#B8862E', rojo: '#B23A48', morado: '#4A1E5C', lila: '#A98BB8', gris: '#8A7690', azul: '#4F7CA8' };

const ESTATUS = {
  cumplida: { label: 'Cumplida', tipo: 'verde', color: COLOR.verde },
  tarde: { label: 'Tarde', tipo: 'amarillo', color: COLOR.amarillo },
  incompleta: { label: 'Incompleta', tipo: 'rojo', color: COLOR.rojo },
  por_vencer: { label: 'Por vencer', tipo: 'gris', color: COLOR.lila },
  sin_fecha: { label: 'Sin fecha prometida', tipo: 'azul', color: COLOR.azul },
  cerrada: { label: 'Cancelada (cerrada sin recibir)', tipo: 'gris', color: COLOR.gris },
};
const LEYENDA_SIN_FECHA = 'Compras debe capturar la fecha prometida en NetSuite para que la línea se evalúe.';

// ─── Helpers ───
const num = (v) => (v === null || v === undefined || v === '' ? null : parseFloat(v));
const fmtPct = (v) => (num(v) === null ? '—' : `${num(v).toFixed(1)}%`);
const fmtNum = (v) => (num(v) === null ? '—' : num(v).toLocaleString('es-MX', { maximumFractionDigits: 2 }));
const fmtFecha = (d) => (d ? new Date(d).toLocaleDateString('es-MX') : '—');
const nombreMes = (d) => (d ? new Date(d).toLocaleDateString('es-MX', { month: 'long', year: 'numeric' }) : '—');
const nombreMesCorto = (d) => new Date(d).toLocaleDateString('es-MX', { month: 'short', year: '2-digit' });
const pad = (n) => String(n).padStart(2, '0');

function rangoDeMes(mes) {
  const d = new Date(mes);
  const y = d.getFullYear();
  const m = d.getMonth();
  return { desde: `${y}-${pad(m + 1)}-01`, hasta: `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}` };
}

function semaforoPct(v, meta) {
  if (num(v) === null) return 'gris';
  if (num(v) >= meta) return 'verde';
  if (num(v) >= meta - 10) return 'amarillo';
  return 'rojo';
}

function Grafica({ config, alto = 280 }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return undefined;
    const chart = new Chart(ref.current, config);
    return () => chart.destroy();
  }, [config]);
  return (
    <div style={{ position: 'relative', height: alto }}>
      <canvas ref={ref} />
    </div>
  );
}

const Tag = ({ children, tipo = 'gris', title }) => (
  <span style={{ ...x.tag, ...x[`tag_${tipo}`] }} title={title}>{children}</span>
);

function descargarExcel(filas, hoja, archivo) {
  const ws = XLSX.utils.json_to_sheet(filas);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, hoja);
  XLSX.writeFile(wb, archivo);
}

export default function OtifProveedor() {
  const [tab, setTab] = useState('resumen');
  const [resumen, setResumen] = useState([]);
  const [meta, setMeta] = useState(95);
  const [ultimaSync, setUltimaSync] = useState(null);
  const [departamentos, setDepartamentos] = useState([]);
  const [departamento, setDepartamento] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [mensaje, setMensaje] = useState(null);
  const [sincronizando, setSincronizando] = useState(false);

  // Por proveedor
  const [rangoProv, setRangoProv] = useState({ desde: '', hasta: '' });
  const [proveedores, setProveedores] = useState([]);
  const [cargandoProv, setCargandoProv] = useState(false);

  // Detalle
  const filtrosVacios = { desde: '', hasta: '', estatus: '', proveedor: '', busqueda: '' };
  const [filtros, setFiltros] = useState(filtrosVacios);
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const [detalle, setDetalle] = useState({ data: [], total: 0, totalPaginas: 1 });
  const [cargandoDetalle, setCargandoDetalle] = useState(false);
  const [exportando, setExportando] = useState(false);

  const conDepto = (params) => (departamento ? { ...params, departamento } : params);

  async function cargarResumen(depto = departamento) {
    const res = await api.get(`${BASE}/resumen`, { params: depto ? { departamento: depto } : {} });
    setResumen(res.data.data);
    if (res.data.meta !== null && res.data.meta !== undefined) setMeta(res.data.meta);
    setUltimaSync(res.data.ultimaSync);
    return res.data.data;
  }

  async function cargarProveedores(rango = rangoProv, depto = departamento) {
    try {
      setCargandoProv(true);
      const params = {};
      if (rango.desde) params.desde = rango.desde;
      if (rango.hasta) params.hasta = rango.hasta;
      if (depto) params.departamento = depto;
      const res = await api.get(`${BASE}/por-proveedor`, { params });
      setProveedores(res.data.data);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar el scorecard por proveedor.' });
    } finally {
      setCargandoProv(false);
    }
  }

  async function cargarDetalle(f = filtros, p = pagina, pp = porPagina, depto = departamento) {
    try {
      setCargandoDetalle(true);
      const params = { pagina: p, porPagina: pp };
      Object.entries(f).forEach(([k, v]) => { if (v) params[k] = v; });
      if (depto) params.departamento = depto;
      const res = await api.get(`${BASE}/detalle`, { params });
      setDetalle({ data: res.data.data, total: res.data.total, totalPaginas: res.data.totalPaginas });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo cargar el detalle.' });
    } finally {
      setCargandoDetalle(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        setCargando(true);
        const [filas, deps] = await Promise.all([cargarResumen(''), api.get(`${BASE}/departamentos`).then((r) => r.data.data).catch(() => [])]);
        setDepartamentos(deps);
        const cerrado = filas.find((r) => !r.es_parcial && Number(r.evaluables) > 0) || filas[0];
        if (cerrado) {
          const rango = rangoDeMes(cerrado.mes);
          setRangoProv(rango);
          const f = { ...filtrosVacios, ...rango };
          setFiltros(f);
          await Promise.all([cargarProveedores(rango, ''), cargarDetalle(f, 1, porPagina, '')]);
        }
        setError(null);
      } catch (err) {
        console.error(err);
        setError('No se pudo cargar el KPI de OTIF Proveedor.');
      } finally {
        setCargando(false);
      }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function cambiarDepartamento(depto) {
    setDepartamento(depto);
    await Promise.all([cargarResumen(depto), cargarProveedores(rangoProv, depto), cargarDetalle(filtros, 1, porPagina, depto)]);
    setPagina(1);
  }

  async function handleSincronizar() {
    try {
      setSincronizando(true);
      setMensaje(null);
      const res = await api.post('/api/kpis/sync/otif-proveedor');
      await Promise.all([cargarResumen(), cargarProveedores(), cargarDetalle()]);
      const r = res.data.resultado;
      const aviso = r.advertencias?.length ? ` Aviso: ${r.advertencias.join(' ')}` : '';
      setMensaje({ tipo: r.advertencias?.length ? 'error' : 'ok', texto: `Sincronizado: ${r.insertados} líneas nuevas, ${r.actualizados} actualizadas.${aviso}` });
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: err.response?.data?.error || 'No se pudo sincronizar con NetSuite.' });
    } finally {
      setSincronizando(false);
    }
  }

  function irADetalle(extra) {
    const f = { ...filtrosVacios, ...extra };
    setFiltros(f);
    setPagina(1);
    setTab('detalle');
    cargarDetalle(f, 1);
  }

  // ─── Derivados ───
  const ordenAsc = useMemo(() => [...resumen].reverse(), [resumen]);
  const mesCerrado = resumen.find((r) => !r.es_parcial && Number(r.evaluables) > 0) || null;
  const idxCerrado = mesCerrado ? resumen.indexOf(mesCerrado) : -1;
  const previo = idxCerrado >= 0 ? resumen.slice(idxCerrado + 1).find((r) => Number(r.evaluables) > 0) : null;
  const mesEnCurso = resumen.find((r) => r.es_parcial) || null;
  const etiqueta = (r) => nombreMesCorto(r.mes) + (r.es_parcial ? '*' : '');
  const conEvaluables = ordenAsc.filter((r) => Number(r.evaluables) > 0);

  const configPorcentajes = useMemo(() => ({
    type: 'line',
    data: {
      labels: conEvaluables.map(etiqueta),
      datasets: [
        { label: '% OTIF', data: conEvaluables.map((r) => num(r.pct_otif)), borderColor: COLOR.morado, backgroundColor: COLOR.morado, borderWidth: 2.5, pointRadius: 5, tension: 0.25,
          pointBackgroundColor: conEvaluables.map((r) => (r.es_parcial ? '#fff' : COLOR[semaforoPct(r.pct_otif, meta)])) },
        { label: '% In Full (completas)', data: conEvaluables.map((r) => num(r.pct_in_full)), borderColor: COLOR.azul, backgroundColor: COLOR.azul, pointRadius: 3, tension: 0.25 },
        { label: '% On Time (a tiempo)', data: conEvaluables.map((r) => num(r.pct_on_time)), borderColor: COLOR.amarillo, backgroundColor: COLOR.amarillo, pointRadius: 3, tension: 0.25 },
        { label: `Meta (${meta}%)`, data: conEvaluables.map(() => meta), borderColor: COLOR.verde, borderDash: [6, 4], borderWidth: 1.5, pointRadius: 0 },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { y: { min: 0, max: 100, ticks: { callback: (v) => `${v}%` } } },
      plugins: { legend: { position: 'bottom' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${fmtPct(c.raw)}` } } },
    },
  }), [conEvaluables, meta]); // eslint-disable-line react-hooks/exhaustive-deps

  const configLineas = useMemo(() => ({
    type: 'bar',
    data: {
      labels: ordenAsc.map(etiqueta),
      datasets: [
        { label: 'Cumplidas', data: ordenAsc.map((r) => Number(r.cumplidas)), backgroundColor: COLOR.verde },
        { label: 'Tarde', data: ordenAsc.map((r) => Number(r.tarde)), backgroundColor: COLOR.amarillo },
        { label: 'Incompletas', data: ordenAsc.map((r) => Number(r.incompletas)), backgroundColor: COLOR.rojo },
        { label: 'Por vencer', data: ordenAsc.map((r) => Number(r.por_vencer)), backgroundColor: '#D9CDE0' },
        { label: 'Sin fecha prometida', data: ordenAsc.map((r) => Number(r.sin_fecha)), backgroundColor: COLOR.azul },
      ],
    },
    options: {
      maintainAspectRatio: false,
      scales: { x: { stacked: true }, y: { stacked: true, title: { display: true, text: 'Líneas de OC' } } },
      plugins: { legend: { position: 'bottom' } },
    },
  }), [ordenAsc]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Excel ───
  function excelHistorico() {
    descargarExcel(ordenAsc.map((r) => ({
      Mes: nombreMes(r.mes) + (r.es_parcial ? ' (parcial)' : ''),
      '% OTIF': num(r.pct_otif), '% In Full': num(r.pct_in_full), '% On Time': num(r.pct_on_time), Meta: meta,
      Evaluables: Number(r.evaluables), Cumplidas: Number(r.cumplidas), Tarde: Number(r.tarde), Incompletas: Number(r.incompletas),
      'Por vencer': Number(r.por_vencer), 'Sin fecha prometida (mes de la OC)': Number(r.sin_fecha),
      'Canceladas (cerradas sin recibir)': Number(r.cerradas), 'Llegó de más': Number(r.sobre_entrega),
    })), 'Histórico', 'OTIF_proveedor_historico.xlsx');
  }

  function excelProveedores() {
    descargarExcel(proveedores.map((p) => ({
      Proveedor: p.proveedor, '% OTIF': num(p.pct_otif), '% In Full': num(p.pct_in_full), Evaluables: Number(p.evaluables),
      Cumplidas: Number(p.cumplidas), Tarde: Number(p.tarde), Incompletas: Number(p.incompletas),
      'Días de atraso promedio': num(p.dias_atraso_prom), 'Por vencer': Number(p.por_vencer), 'Sin fecha prometida': Number(p.sin_fecha),
    })), 'Por proveedor', `OTIF_por_proveedor${rangoProv.desde ? `_${rangoProv.desde}_a_${rangoProv.hasta}` : ''}.xlsx`);
  }

  async function excelDetalle() {
    try {
      setExportando(true);
      const params = conDepto({ exportar: '1' });
      Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await api.get(`${BASE}/detalle`, { params });
      descargarExcel(res.data.data.map((l) => ({
        OC: l.numero_oc, 'Fecha OC': fmtFecha(l.fecha_oc), Proveedor: l.proveedor || '', 'Artículo': l.articulo, Clase: l.clase || '',
        'Cant. pedida': num(l.qty_solicitada), 'Cant. recibida': num(l.qty_recibida), 'Cant. rechazada': num(l.qty_rechazada),
        '% Cumplimiento': num(l.pct_cumplimiento), 'Fecha prometida': fmtFecha(l.fecha_prometida),
        'Primera recepción': fmtFecha(l.fecha_primera_recepcion), 'Última recepción': fmtFecha(l.fecha_recepcion),
        'Se completó': fmtFecha(l.fecha_completada), Estatus: ESTATUS[l.estatus]?.label || l.estatus,
        'Días de atraso': l.dias_atraso, 'Llegó de más': l.sobre_entrega ? 'Sí' : '', 'Línea cerrada': l.linea_cerrada ? 'Sí' : '',
        Observación: l.estatus === 'sin_fecha' ? LEYENDA_SIN_FECHA : '',
      })), 'Detalle', `OTIF_detalle${filtros.desde ? `_${filtros.desde}_a_${filtros.hasta}` : ''}.xlsx`);
    } catch (err) {
      console.error(err);
      setMensaje({ tipo: 'error', texto: 'No se pudo generar el Excel.' });
    } finally {
      setExportando(false);
    }
  }

  if (cargando) return <div style={s.paginaContenedor}>Cargando...</div>;
  if (error) return <div style={{ ...s.paginaContenedor, color: COLOR.rojo }}>{error}</div>;

  const valorMes = (f) => (resumen.some((r) => { const g = rangoDeMes(r.mes); return g.desde === f.desde && g.hasta === f.hasta; }) ? f.desde : '');
  const selectorMes = (valor, onCambio) => (
    <select style={s.filtroInput} value={valor} onChange={(e) => onCambio(e.target.value ? rangoDeMes(e.target.value + 'T00:00:00') : { desde: '', hasta: '' })}>
      <option value="">Todo / rango personalizado</option>
      {resumen.map((r) => <option key={r.mes} value={rangoDeMes(r.mes).desde}>{nombreMes(r.mes)}{r.es_parcial ? ' (parcial)' : ''}</option>)}
    </select>
  );

  return (
    <div style={s.paginaContenedor} className="contenedor-captura">
      <div style={{ ...s.headerFila, flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
        <div>
          <h1 style={{ ...s.tituloPagina, marginBottom: 2 }}>OTIF Proveedor</h1>
          <div style={{ fontSize: 12, color: COLOR.gris }}>
            Última sincronización con NetSuite: {ultimaSync ? new Date(ultimaSync).toLocaleString('es-MX') : 'sin registro'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {departamentos.length > 1 && (
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Departamento</span>
              <select style={s.filtroInput} value={departamento} onChange={(e) => cambiarDepartamento(e.target.value)}>
                <option value="">Todos</option>
                {departamentos.map((d) => <option key={d.departamento} value={d.departamento}>{d.departamento}</option>)}
              </select>
            </div>
          )}
          <button onClick={handleSincronizar} disabled={sincronizando} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
            <FiRefreshCw /> {sincronizando ? 'Sincronizando...' : 'Sincronizar con NetSuite'}
          </button>
        </div>
      </div>

      {mensaje && <div style={{ ...s.mensaje, color: mensaje.tipo === 'ok' ? COLOR.verde : COLOR.rojo }}>{mensaje.texto}</div>}

      <div style={s.tabBar}>
        {[['resumen', 'Resumen'], ['historico', 'Histórico'], ['proveedores', 'Por proveedor'], ['detalle', 'Detalle de líneas']].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...s.tabBtn, ...(tab === k ? s.tabBtnActivo : {}) }}>{label}</button>
        ))}
      </div>

      {/* ═══════════ RESUMEN ═══════════ */}
      {tab === 'resumen' && (
        <>
          {!mesCerrado && <div style={s.subtitulo}>Todavía no hay líneas evaluables.</div>}
          {mesCerrado && (
            <>
              <div style={{ ...s.kLbl, marginBottom: 10 }}>Mes cerrado (por fecha prometida): {nombreMes(mesCerrado.mes)}</div>
              <div style={s.kgrid}>
                <TarjetaPct titulo="% OTIF" valor={mesCerrado.pct_otif} previo={previo?.pct_otif} meta={meta} principal
                  sub={`${mesCerrado.cumplidas} de ${mesCerrado.evaluables} líneas completas en la fecha prometida o antes.`} />
                <TarjetaPct titulo="% In Full (completas)" valor={mesCerrado.pct_in_full} previo={previo?.pct_in_full}
                  sub={`${Number(mesCerrado.cumplidas) + Number(mesCerrado.tarde)} líneas llegaron completas, aunque algunas tarde.`} />
                <TarjetaPct titulo="% On Time (a tiempo)" valor={mesCerrado.pct_on_time} previo={previo?.pct_on_time}
                  sub="La última entrega llegó en la fecha prometida o antes, completa o no." />
                <div style={{ ...s.kcard, borderTop: `3px solid ${COLOR.azul}` }}>
                  <div style={s.kLbl}>Sin fecha prometida</div>
                  <div style={{ ...s.kVal, fontSize: 32, color: COLOR.azul }}>{mesCerrado.sin_fecha}</div>
                  <div style={{ ...x.veredicto, color: COLOR.gris }}>líneas de OCs de {nombreMes(mesCerrado.mes)}</div>
                  <div style={s.kSub}>{LEYENDA_SIN_FECHA}</div>
                  <button style={{ ...s.linkChico, marginTop: 8, padding: 0 }} onClick={() => irADetalle({ ...rangoDeMes(mesCerrado.mes), estatus: 'sin_fecha' })}>Ver cuáles</button>
                </div>
              </div>

              <div style={{ ...s.tarjeta, display: 'flex', gap: 22, flexWrap: 'wrap', fontSize: 13, alignItems: 'center' }}>
                <span><strong style={{ color: COLOR.amarillo }}>{mesCerrado.tarde}</strong> tarde</span>
                <span><strong style={{ color: COLOR.rojo }}>{mesCerrado.incompletas}</strong> incompletas</span>
                <span><strong>{mesCerrado.por_vencer}</strong> por vencer</span>
                <span><strong>{mesCerrado.cerradas}</strong> canceladas (no cuentan)</span>
                <span><strong>{mesCerrado.sobre_entrega}</strong> llegaron de más</span>
                <button style={s.linkChico} onClick={() => irADetalle({ ...rangoDeMes(mesCerrado.mes), estatus: 'no_cumplida' })}>Ver las no cumplidas</button>
                <button style={s.linkChico} onClick={() => { setRangoProv(rangoDeMes(mesCerrado.mes)); cargarProveedores(rangoDeMes(mesCerrado.mes)); setTab('proveedores'); }}>Ver por proveedor</button>
              </div>
            </>
          )}

          {mesEnCurso && (
            <div style={{ ...s.tarjeta, borderTop: `3px dashed ${COLOR.gris}` }}>
              <div style={s.kLbl}>En curso: {nombreMes(mesEnCurso.mes)} <Tag tipo="amarillo">parcial, día {new Date().getDate()}</Tag></div>
              <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap', fontSize: 13, marginTop: 6 }}>
                <span>OTIF hasta hoy: <strong>{fmtPct(mesEnCurso.pct_otif)}</strong> ({mesEnCurso.cumplidas} de {mesEnCurso.evaluables} evaluables)</span>
                <span>Por vencer: <strong>{mesEnCurso.por_vencer}</strong></span>
                <span>Sin fecha prometida: <strong>{mesEnCurso.sin_fecha}</strong></span>
                <button style={s.linkChico} onClick={() => irADetalle(rangoDeMes(mesEnCurso.mes))}>Ver líneas</button>
              </div>
            </div>
          )}

          <div style={s.seccionKpiBloque}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <FiInfo style={{ color: COLOR.morado, marginTop: 2, flexShrink: 0 }} />
              <div style={s.seccionKpiFormula}>
                <strong style={{ color: '#2A1830' }}>Cómo se calcula.</strong> Cada línea de OC de materia prima y empaque se evalúa en el mes de su fecha prometida.
                Es <strong>OTIF</strong> si se recibió el 100% en la fecha prometida o antes (con entregas parciales cuenta el día en que se juntó el 100%).
                Si llega de más cuenta como completa; lo que entra al Almacén de Rechazo no cuenta como recibido.
                No cumplen: las que llegaron completas pero <strong>tarde</strong>, y las <strong>incompletas</strong> (ya vencieron sin completarse,
                o se cerraron con entrega parcial porque el proveedor no completó). Meta: {meta}% o más.
                <br />
                <strong style={{ color: '#2A1830' }}>No se evalúan:</strong> las que todavía no vencen, las que no tienen fecha prometida (Compras debe capturarla)
                y las cerradas sin nada recibido (las canceló Dasavena).
                <br />
                <strong style={{ color: '#2A1830' }}>De dónde sale.</strong> NetSuite: líneas de OC (customsearch729) y sus recepciones (customsearch730), cruzadas por la clave de línea.
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════════ HISTÓRICO ═══════════ */}
      {tab === 'historico' && (
        <>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>OTIF, In Full y On Time por mes</div>
            <Grafica config={configPorcentajes} />
            <div style={{ ...s.kSub, marginTop: 8 }}>* Mes en curso, parcial. Solo meses con líneas evaluables.</div>
          </div>
          <div style={s.tarjeta}>
            <div style={s.seccionTitulo}>Líneas por estatus</div>
            <Grafica config={configLineas} alto={260} />
            <div style={{ ...s.kSub, marginTop: 8 }}>Las líneas sin fecha prometida se cuentan en el mes de la OC.</div>
          </div>

          <div style={{ ...s.headerFila, margin: '6px 0 8px' }}>
            <h3 style={s.subtituloLista}>Tabla mensual</h3>
            <button onClick={excelHistorico} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}><FiDownload /> Descargar Excel</button>
          </div>
          <div style={s.subtitulo}>Haz clic en un mes para ver sus líneas.</div>
          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Mes</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% OTIF</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% In Full</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% On Time</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Evaluables</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Tarde</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Incompletas</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Por vencer</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Sin fecha</th>
                </tr>
              </thead>
              <tbody>
                {resumen.map((r) => (
                  <tr key={r.mes} onClick={() => irADetalle(rangoDeMes(r.mes))} style={{ cursor: 'pointer' }}>
                    <td style={s.td}>{nombreMes(r.mes)}{r.es_parcial && <Tag tipo="amarillo">parcial</Tag>}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: r.es_parcial ? COLOR.gris : COLOR[semaforoPct(r.pct_otif, meta)] }}>{fmtPct(r.pct_otif)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{fmtPct(r.pct_in_full)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{fmtPct(r.pct_on_time)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{r.evaluables}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{r.tarde}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{r.incompletas}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{r.por_vencer}</td>
                    <td style={{ ...s.td, textAlign: 'right', color: Number(r.sin_fecha) > 0 ? COLOR.azul : undefined }}>{r.sin_fecha}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════ POR PROVEEDOR ═══════════ */}
      {tab === 'proveedores' && (
        <>
          <div style={s.filtrosBar}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mes (fecha prometida)</span>
              {selectorMes(valorMes(rangoProv), (r) => { setRangoProv(r); cargarProveedores(r); })}
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={rangoProv.desde} onChange={(e) => setRangoProv({ ...rangoProv, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={rangoProv.hasta} onChange={(e) => setRangoProv({ ...rangoProv, hasta: e.target.value })} />
            </div>
            <button style={s.botonBuscar2} onClick={() => cargarProveedores()}>Buscar</button>
            <button onClick={excelProveedores} disabled={proveedores.length === 0} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
              <FiDownload /> Descargar Excel
            </button>
          </div>
          <div style={s.subtitulo}>Del que peor cumple al que mejor. Haz clic en un proveedor para ver sus líneas.</div>
          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>Proveedor</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% OTIF</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% In Full</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Evaluables</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Cumplidas</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Tarde</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Incompletas</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Atraso prom.</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Por vencer</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Sin fecha</th>
                </tr>
              </thead>
              <tbody>
                {cargandoProv && <tr><td colSpan={10} style={{ ...s.td, color: COLOR.gris }}>Cargando...</td></tr>}
                {!cargandoProv && proveedores.length === 0 && <tr><td colSpan={10} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>Sin datos para este periodo.</td></tr>}
                {!cargandoProv && proveedores.map((p) => (
                  <tr key={p.proveedor} onClick={() => irADetalle({ ...rangoProv, proveedor: p.proveedor })} style={{ cursor: 'pointer' }}>
                    <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 200 }}>{p.proveedor}</td>
                    <td style={{ ...s.td, textAlign: 'right', fontWeight: 700, color: COLOR[semaforoPct(p.pct_otif, meta)] }}>{fmtPct(p.pct_otif)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{fmtPct(p.pct_in_full)}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{p.evaluables}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{p.cumplidas}</td>
                    <td style={{ ...s.td, textAlign: 'right', color: Number(p.tarde) ? COLOR.amarillo : undefined }}>{p.tarde}</td>
                    <td style={{ ...s.td, textAlign: 'right', color: Number(p.incompletas) ? COLOR.rojo : undefined }}>{p.incompletas}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{num(p.dias_atraso_prom) === null ? '—' : `${fmtNum(p.dias_atraso_prom)} días`}</td>
                    <td style={{ ...s.td, textAlign: 'right' }}>{p.por_vencer}</td>
                    <td style={{ ...s.td, textAlign: 'right', color: Number(p.sin_fecha) ? COLOR.azul : undefined }}>{p.sin_fecha}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ═══════════ DETALLE ═══════════ */}
      {tab === 'detalle' && (
        <>
          <div style={s.filtrosBar}>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Mes</span>
              {selectorMes(valorMes(filtros), (r) => setFiltros({ ...filtros, ...r }))}
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Desde</span>
              <input type="date" style={s.filtroInput} value={filtros.desde} onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Hasta</span>
              <input type="date" style={s.filtroInput} value={filtros.hasta} onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })} />
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Estatus</span>
              <select style={s.filtroInput} value={filtros.estatus} onChange={(e) => setFiltros({ ...filtros, estatus: e.target.value })}>
                <option value="">Todos</option>
                <option value="no_cumplida">No cumplidas (tarde + incompletas)</option>
                {Object.entries(ESTATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div style={s.filtroGrupo}>
              <span style={s.filtroLabel}>Buscar</span>
              <input
                type="text"
                placeholder="OC, proveedor o artículo"
                style={{ ...s.filtroInput, minWidth: 190 }}
                value={filtros.busqueda}
                onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { setPagina(1); cargarDetalle(filtros, 1); } }}
              />
            </div>
            <button style={s.botonBuscar2} onClick={() => { setPagina(1); cargarDetalle(filtros, 1); }}>Buscar</button>
            <button style={s.linkChico} onClick={() => { setFiltros(filtrosVacios); setPagina(1); cargarDetalle(filtrosVacios, 1); }}>Limpiar filtros</button>
          </div>

          {filtros.proveedor && (
            <div style={{ ...s.avisoAdvertencia, marginTop: 0, marginBottom: 10, display: 'flex', gap: 10, alignItems: 'center' }}>
              Proveedor: <strong>{filtros.proveedor}</strong>
              <button style={s.linkChico} onClick={() => { const f = { ...filtros, proveedor: '' }; setFiltros(f); setPagina(1); cargarDetalle(f, 1); }}>quitar</button>
            </div>
          )}

          <div style={{ ...s.headerFila, marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 12, color: COLOR.gris }}>
              {cargandoDetalle ? 'Cargando...' : `${detalle.total.toLocaleString('es-MX')} líneas`} · Las fechas filtran por fecha prometida (o fecha de la OC si no tiene).
            </div>
            <button onClick={excelDetalle} disabled={exportando || detalle.total === 0} style={{ ...s.botonGuardarChico, display: 'flex', alignItems: 'center', gap: 6 }}>
              <FiDownload /> {exportando ? 'Generando...' : 'Descargar Excel'}
            </button>
          </div>

          <div style={s.tablaScroll}>
            <table style={s.tabla}>
              <thead>
                <tr>
                  <th style={s.th}>OC</th>
                  <th style={s.th}>Proveedor</th>
                  <th style={s.th}>Artículo</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Pedido</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Recibido</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>% Cumpl.</th>
                  <th style={s.th}>Prometida</th>
                  <th style={s.th}>Se completó</th>
                  <th style={s.th}>Estatus</th>
                </tr>
              </thead>
              <tbody>
                {!cargandoDetalle && detalle.data.length === 0 && (
                  <tr><td colSpan={9} style={{ ...s.td, color: COLOR.gris, padding: 16 }}>No hay líneas con estos filtros.</td></tr>
                )}
                {detalle.data.map((l, i) => {
                  const est = ESTATUS[l.estatus] || { label: l.estatus, tipo: 'gris' };
                  const nuevaOC = i === 0 || detalle.data[i - 1].numero_oc !== l.numero_oc;
                  return (
                    <tr key={l.clave_linea_oc} style={{ ...(nuevaOC && i > 0 ? { borderTop: '2px solid #E6DDD2' } : {}), ...(l.estatus === 'incompleta' ? { background: '#FDF3F1' } : l.estatus === 'sin_fecha' ? { background: '#F3F7FB' } : {}) }}>
                      <td style={s.td}>
                        {nuevaOC ? <><strong>{l.numero_oc}</strong><div style={x.sub}>{fmtFecha(l.fecha_oc)}</div></> : <span style={x.sub}>〃</span>}
                      </td>
                      <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 160 }}>{nuevaOC ? l.proveedor || '—' : ''}</td>
                      <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 160 }}>
                        {l.articulo}
                        {l.clase && l.clase !== 'Materia Prima' && <div style={x.sub}>{l.clase}</div>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right' }}>{fmtNum(l.qty_solicitada)}</td>
                      <td style={{ ...s.td, textAlign: 'right' }}>
                        {fmtNum(l.qty_recibida)}
                        {num(l.qty_rechazada) > 0 && <div style={{ ...x.sub, color: COLOR.rojo }}>{fmtNum(l.qty_rechazada)} rechazado</div>}
                        {Number(l.num_recepciones) > 1 && <div style={x.sub}>{l.num_recepciones} entregas</div>}
                      </td>
                      <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{fmtPct(l.pct_cumplimiento)}</td>
                      <td style={s.td}>{fmtFecha(l.fecha_prometida)}</td>
                      <td style={s.td}>
                        {fmtFecha(l.fecha_completada)}
                        {!l.fecha_completada && l.fecha_recepcion && <div style={x.sub}>última entrega {fmtFecha(l.fecha_recepcion)}</div>}
                      </td>
                      <td style={{ ...s.td, whiteSpace: 'normal', minWidth: 150 }}>
                        <Tag tipo={est.tipo}>{est.label}</Tag>
                        {num(l.dias_atraso) > 0 && ['tarde', 'incompleta'].includes(l.estatus) && <Tag tipo="rojo">{l.dias_atraso} días de atraso</Tag>}
                        {l.sobre_entrega && <Tag tipo="azul">llegó de más</Tag>}
                        {l.linea_cerrada && l.estatus === 'incompleta' && <Tag title="Se cerró la línea porque el proveedor no completó">cerrada sin completar</Tag>}
                        {l.estatus === 'sin_fecha' && <div style={{ ...x.sub, color: COLOR.azul, marginTop: 3 }}><FiAlertTriangle size={10} /> {LEYENDA_SIN_FECHA}</div>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div style={x.paginacion}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={s.filtroLabel}>Por página</span>
              <select style={s.inputChico} value={porPagina} onChange={(e) => { const pp = Number(e.target.value); setPorPagina(pp); setPagina(1); cargarDetalle(filtros, 1, pp); }}>
                {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={x.botonPagina} disabled={pagina <= 1 || cargandoDetalle} onClick={() => { const p = pagina - 1; setPagina(p); cargarDetalle(filtros, p); }} aria-label="Página anterior"><FiChevronLeft /></button>
              <span style={{ fontSize: 12 }}>Página {pagina} de {detalle.totalPaginas}</span>
              <button style={x.botonPagina} disabled={pagina >= detalle.totalPaginas || cargandoDetalle} onClick={() => { const p = pagina + 1; setPagina(p); cargarDetalle(filtros, p); }} aria-label="Página siguiente"><FiChevronRight /></button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Tarjeta de porcentaje ───
function TarjetaPct({ titulo, valor, previo, meta, sub, principal }) {
  const v = num(valor);
  const sem = principal ? semaforoPct(v, meta) : null;
  const color = principal ? COLOR[sem] : COLOR.morado;
  const d = v !== null && num(previo) !== null ? v - num(previo) : null;
  return (
    <div style={{ ...s.kcard, borderTop: `3px solid ${color}` }}>
      <div style={s.kLbl}>{titulo}</div>
      <div style={{ ...s.kVal, fontSize: 32, color }}>{fmtPct(v)}</div>
      {principal && (
        <div style={{ ...x.veredicto, color }}>
          {v === null ? 'Sin datos' : v >= meta ? `Cumple la meta de ${meta}%` : `No cumple la meta de ${meta}% (${(v - meta).toFixed(1)} pts)`}
        </div>
      )}
      <div style={s.kSub}>{sub}</div>
      {d !== null && (
        <div style={{ ...s.kSub, color: d > 0 ? COLOR.verde : d < 0 ? COLOR.rojo : COLOR.gris }}>
          {d > 0 ? '▲' : d < 0 ? '▼' : '='} {Math.abs(d).toFixed(1)} pts vs mes anterior ({fmtPct(previo)})
        </div>
      )}
    </div>
  );
}

const x = {
  veredicto: { fontSize: 12.5, fontWeight: 600, margin: '6px 0 6px' },
  sub: { fontSize: 10.5, color: '#8A7690' },
  tag: { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 10, marginRight: 4, marginTop: 2, whiteSpace: 'nowrap', textTransform: 'none', letterSpacing: 0 },
  tag_gris: { color: '#6E4F80', background: '#F0E9E0' },
  tag_verde: { color: '#2F6146', background: '#E3F0E8' },
  tag_amarillo: { color: '#7a5417', background: '#F9F0DD' },
  tag_rojo: { color: '#B23A48', background: '#F7E5E7' },
  tag_azul: { color: '#2F5D8A', background: '#E3EDF7' },
  paginacion: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginTop: 12 },
  botonPagina: { width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #E6DDD2', background: '#fff', borderRadius: 6, cursor: 'pointer', color: '#4A1E5C' },
};